/**
 * The system registry of the current tenant (docs/systems.md), loaded once before the app renders (like the catalog) so
 * every systemName / systemColor / SystemBadge says what the admins set from the first paint: the systems go into the
 * registry of lib/systems.ts. It loads the registry alone (`detect=0`), which the Cloud answers from its database at once;
 * the Systems page asks for the data models of each system itself. After an edit the registry changes at once and
 * `version` grows, so the pages that use it render again.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Api } from './api.ts';
import { setSystem, setSystems, type SystemInfo, type SystemStatus } from './systems.ts';
import { Spinner } from '../components/ui.tsx';

interface SystemsState {
  /** By code. */
  systems: ReadonlyMap<string, SystemInfo>;
  version: number;
  reload: () => Promise<void>;
  /** Puts an edited system in place (`was`: its code before, when it changed). */
  apply: (s: SystemInfo, was?: string) => void;
  applyStatus: (codes: string[], status: SystemStatus) => void;
}

const EMPTY: SystemsState = { systems: new Map(), version: 0, reload: async () => undefined, apply: () => undefined, applyStatus: () => undefined };
const SystemsContext = createContext<SystemsState>(EMPTY);

export const useSystems = (): SystemsState => useContext(SystemsContext);

export function SystemsProvider({ tenantId, children }: { tenantId: string; children: ReactNode }) {
  const [data, setData] = useState<{ tid: string; systems: Map<string, SystemInfo>; version: number }>();
  const [failed, setFailed] = useState<string>();
  const latest = useRef(0);

  const take = useCallback((tid: string, list: SystemInfo[]) => {
    setSystems(list);
    setData((prev) => ({ tid, systems: new Map(list.map((s) => [s.code, s])), version: (prev?.version ?? 0) + 1 }));
  }, []);
  const reload = useCallback(async () => {
    const ticket = ++latest.current;
    try {
      const d = await Api.systems(tenantId, false);
      if (ticket === latest.current) take(tenantId, d.systems);
    } catch {
      // Without the registry the app still works with the codes and their default colours
      if (ticket === latest.current) { setSystems([]); setFailed(tenantId); }
    }
  }, [tenantId, take]);
  useEffect(() => {
    void reload();
    // Systems keep turning up in new data: the count of those to check stays fresh
    const t = setInterval(() => { if (document.visibilityState === 'visible') void reload(); }, 60_000);
    return () => clearInterval(t);
  }, [reload]);

  const apply = useCallback((s: SystemInfo, was?: string) => {
    setSystem(s, was);
    setData((prev) => {
      if (!prev) return prev;
      const systems = new Map([...prev.systems].filter(([code]) => code !== was && !s.aliases.includes(code)));
      systems.set(s.code, { ...s, classes: s.classes.length ? s.classes : prev.systems.get(was ?? s.code)?.classes ?? [] });
      return { ...prev, systems, version: prev.version + 1 };
    });
  }, []);
  const applyStatus = useCallback((codes: string[], status: SystemStatus) => {
    setData((prev) => {
      if (!prev) return prev;
      const systems = new Map(prev.systems);
      for (const c of codes) { const s = systems.get(c); if (s) systems.set(c, { ...s, status, reviewedAt: status === 'new' ? null : Date.now() }); }
      setSystems(systems.values());
      return { ...prev, systems, version: prev.version + 1 };
    });
  }, []);

  const ready = data?.tid === tenantId || failed === tenantId;
  const value = useMemo<SystemsState>(() => (data?.tid === tenantId
    ? { systems: data.systems, version: data.version, reload, apply, applyStatus }
    : { ...EMPTY, reload, apply, applyStatus }), [data, tenantId, reload, apply, applyStatus]);

  if (!ready) return <div className="min-h-screen bg-n1 grid place-items-center"><Spinner /></div>;
  return <SystemsContext.Provider value={value}>{children}</SystemsContext.Provider>;
}

/** How many systems are new (the badge of Settings → Systems). */
export function useNewSystemsCount(): number {
  const { systems } = useSystems();
  return useMemo(() => [...systems.values()].filter((s) => s.status === 'new').length, [systems]);
}
